import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const UserScanner = () => {
  const [scanStatus, setScanStatus] = useState(null); // null, 'success', 'error', 'duplicate', 'not-registered'
  const [statusMessage, setStatusMessage] = useState("");
  const [lastScanned, setLastScanned] = useState("");
  const [lastScanTime, setLastScanTime] = useState(null);
  const [cameraId, setCameraId] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          const backCamera = devices.find((device) =>
            device.label.toLowerCase().includes("back")
          );
          setCameraId(backCamera ? backCamera.id : devices[0].id);
        } else {
          setStatusMessage("No cameras found");
          setScanStatus("error");
        }
      })
      .catch((err) => {
        console.error("Error fetching cameras:", err);
        setStatusMessage("Camera access denied");
        setScanStatus("error");
      });
  }, []);

  useEffect(() => {
    if (cameraId && !scannerRef.current) {
      const html5QrCode = new Html5Qrcode("qr-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      });

      html5QrCode
        .start(
          cameraId,
          { 
            fps: 10, 
            qrbox: { width: 250, height: 250 }, 
            aspectRatio: 1.0 
          },
          (decodedText) => handleScanSuccess(decodedText),
          (error) => {} // Silent error handling for scanning attempts
        )
        .then(() => {
          setScanner(html5QrCode);
          scannerRef.current = html5QrCode;
        })
        .catch((err) => {
          console.error("Error starting scanner:", err);
          setStatusMessage("Failed to start camera");
          setScanStatus("error");
        });
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [cameraId]);

  const [lastApiCallTime, setLastApiCallTime] = useState({});

  const handleScanSuccess = async (decodedText) => {
    // Prevent processing if already processing
    if (isProcessing) return;

    // Validate mobile number format (10 digits)
    const isValidMobileNumber = /^\d{10}$/.test(decodedText);

    if (!isValidMobileNumber) {
      setScanStatus("error");
      setStatusMessage("Invalid QR Code - Must be a 10-digit mobile number");
      setTimeout(() => setScanStatus(null), 3000);
      return;
    }

    setIsProcessing(true);
    setLastScanned(decodedText);

    try {
      const response = await fetch("/api/scans", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ mobileNumber: decodedText }),
      });

      const data = await response.json();

      if (response.ok) {
        setScanStatus("success");
        setStatusMessage("✓ Scan Successful! Entry Registered");
        setLastScanTime(new Date().toLocaleTimeString('en-IN', { 
          hour: '2-digit', 
          minute: '2-digit', 
          second: '2-digit',
          hour12: true 
        }));
        
        // Keep success message for 12 seconds
        setTimeout(() => {
          setScanStatus(null);
          setIsProcessing(false);
        }, 1000);
      } else if (response.status === 400) {
        setScanStatus("duplicate");
        if (data.lastScanTime) {
          const formattedTime = new Date(data.lastScanTime).toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          });
          setStatusMessage(`Already Scanned Today at ${formattedTime}`);
        } else {
          setStatusMessage("Mobile Number Already Scanned Today");
        }
        
        setTimeout(() => {
          setScanStatus(null);
          setIsProcessing(false);
        }, 2000);
      } else if (response.status === 404) {
        setScanStatus("not-registered");
        setStatusMessage("Mobile Number Not Registered - Please Register First");
        setTimeout(() => {
          setScanStatus(null);
          setIsProcessing(false);
        }, 3000);
      } else {
        setScanStatus("error");
        setStatusMessage(data.error || "Scan Failed - Please Try Again");
        setTimeout(() => {
          setScanStatus(null);
          setIsProcessing(false);
        }, 3000);
      }
    } catch (err) {
      console.error("Error saving scan data:", err);
      setScanStatus("error");
      setStatusMessage("Network Error - Please Check Connection");
      setTimeout(() => {
        setScanStatus(null);
        setIsProcessing(false);
      }, 3000);
    }
  };

  const getStatusStyle = () => {
    switch (scanStatus) {
      case "success":
        return { ...styles.statusBox, ...styles.statusSuccess };
      case "duplicate":
        return { ...styles.statusBox, ...styles.statusDuplicate };
      case "not-registered":
        return { ...styles.statusBox, ...styles.statusNotRegistered };
      case "error":
        return { ...styles.statusBox, ...styles.statusError };
      default:
        return { display: "none" };
    }
  };

  const getStatusIcon = () => {
    switch (scanStatus) {
      case "success":
        return "✓";
      case "duplicate":
        return "⚠";
      case "not-registered":
        return "ⓘ";
      case "error":
        return "✕";
      default:
        return "";
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img 
          src="/khasdar.jpg" 
          alt="Khasdar Logo" 
          style={styles.logo}
          className="logo-blink"
        />
        <h1 style={styles.title}>Khasdar Mahotsav</h1>
        <p style={styles.subtitle}>QR Scanner Entry System</p>
      </div>

      <div style={styles.scannerWrapper}>
        <div id="qr-reader" style={styles.qrReader}></div>
        
        {scanStatus && (
          <div style={getStatusStyle()}>
            <span style={styles.statusIcon}>{getStatusIcon()}</span>
            <p style={styles.statusMessage}>{statusMessage}</p>
          </div>
        )}

        {lastScanned && lastScanTime && scanStatus === "success" && (
          <div style={styles.scanInfo}>
            <p style={styles.scanInfoText}>
              <strong>Mobile:</strong> {lastScanned}
            </p>
            <p style={styles.scanInfoText}>
              <strong>Time:</strong> {lastScanTime}
            </p>
          </div>
        )}
      </div>

      <div style={styles.footerContainer}>
        <span style={styles.poweredByText}>
          Powered By
          <img
            src="/bharatOnline.png"
            alt="Bharat Online"
            crossOrigin="anonymous"
            style={styles.footerLogo}
            className="footer-logo-blink"
          />
          Bharat Online
        </span>
      </div>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        
        .logo-blink {
          animation: blink 2s ease-in-out infinite;
        }
        
        .footer-logo-blink {
          animation: blink 2.5s ease-in-out infinite;
        }

        #qr-reader video {
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "100vh",
    height: "100vh",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: "linear-gradient(135deg, #13547a, #80d0c7)",
    padding: "15px",
    color: "#fff",
    overflow: "hidden",
    /* reserve space for the fixed footer (70px) + safe-area inset on mobile */
    paddingBottom: "calc(70px + env(safe-area-inset-bottom, 16px))",
  },
  header: {
    textAlign: "center",
    marginBottom: "10px",
    flexShrink: 0,
  },
  logo: {
    width: "70px",
    height: "70px",
    marginBottom: "5px",
    borderRadius: "50%",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: "5px 0 3px",
    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
    color: "#ffffff",
  },
  subtitle: {
    fontSize: "14px",
    opacity: 0.9,
    margin: "0",
  },
  scannerWrapper: {
    width: "100%",
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    flex: "1 1 auto",
    minHeight: 0,
  },
  qrReader: {
    width: "100%",
    border: "4px solid #fff",
    borderRadius: "15px",
    boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
  },
  statusBox: {
    width: "100%",
    padding: "15px",
    borderRadius: "10px",
    textAlign: "center",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    animation: "slideIn 0.3s ease-out",
  },
  statusSuccess: {
    backgroundColor: "#28a745",
    border: "2px solid #1e7e34",
  },
  statusDuplicate: {
    backgroundColor: "#ffc107",
    border: "2px solid #e0a800",
    color: "#000",
  },
  statusNotRegistered: {
    backgroundColor: "#17a2b8",
    border: "2px solid #138496",
  },
  statusError: {
    backgroundColor: "#dc3545",
    border: "2px solid #bd2130",
  },
  statusIcon: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  statusMessage: {
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
  },
  scanInfo: {
    width: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    padding: "12px",
    borderRadius: "8px",
    backdropFilter: "blur(10px)",
  },
  scanInfoText: {
    fontSize: "14px",
    margin: "5px 0",
  },
  footerContainer: {
    /* fixed footer so it's always visible on mobile */
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "50px", /* footer visual height */
    padding: "10px calc(env(safe-area-inset-left, 16px)) calc(env(safe-area-inset-bottom, 8px)) calc(env(safe-area-inset-right, 16px))",
    textAlign: "center",
    zIndex: 999,
    background: "linear-gradient(135deg, rgba(42,93,135,0.95) 0%, rgba(132, 174, 228, 0.95) 100%)",
  },
  poweredByText: {
    fontSize: "15px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    color: "#fff",
    textShadow: "1px 1px 3px rgba(0, 0, 0, 0.3)",
    letterSpacing: "0.5px",
  },
  footerLogo: {
    height: "28px",
    verticalAlign: "middle",
    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
  },
};

export default UserScanner;
